import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, RefreshCw, ShieldCheck, Sparkles, Headphones, Star, Instagram, Phone } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { waMessage } from "@/components/WhatsAppButton";
import { toast } from "sonner";

const Leaf = ({ className }) => (
  <svg viewBox="0 0 200 300" className={className} fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
    <path d="M100 290 C100 200 100 120 100 20" />
    <path d="M100 60 C60 55 40 30 45 5 C80 15 100 35 100 60Z" />
    <path d="M100 110 C140 105 160 80 155 55 C120 65 100 85 100 110Z" />
    <path d="M100 160 C60 155 40 130 45 105 C80 115 100 135 100 160Z" />
    <path d="M100 210 C140 205 160 180 155 155 C120 165 100 185 100 210Z" />
  </svg>
);

const SectionHeading = ({ title, link, linkText = "View All →", center }) => (
  <div className={`mb-8 flex items-end ${center ? "flex-col text-center" : "justify-between"}`}>
    <div>
      <h2 className="font-serif text-3xl text-charcoal sm:text-4xl">{title}</h2>
      <span className="mt-2 block h-0.5 w-16 bg-burgundy" />
    </div>
    {link && <Link to={link} className="text-sm font-medium text-burgundy hover:underline" data-testid="section-viewall">{linkText}</Link>}
  </div>
);

const CATEGORY_LIST = [
  { name: "Kurtis", slug: "kurtis" }, { name: "Dresses", slug: "dresses" }, { name: "Tops", slug: "tops" },
  { name: "Co-ord Sets", slug: "co-ord-sets" }, { name: "Traditional Wear", slug: "traditional-wear" },
  { name: "Sandals", slug: "sandals" }, { name: "Heels", slug: "heels" }, { name: "Flats", slug: "flats" },
  { name: "Casual Footwear", slug: "casual-footwear" },
];

const BENEFITS = [
  { icon: Truck, title: "Free Shipping", text: "On Orders Above ₹999" },
  { icon: RefreshCw, title: "Easy Returns", text: "Simple Returns & Exchange" },
  { icon: ShieldCheck, title: "100% Original", text: "Authentic Products" },
  { icon: Sparkles, title: "Latest Trends", text: "Curated Fashion Collections" },
  { icon: Headphones, title: "Dedicated Support", text: "We're Here to Help" },
];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const r = await api.post("/newsletter", { email }); toast.success(r.data.message || "Subscribed!"); setEmail(""); }
    catch { toast.error("Please enter a valid email"); }
    finally { setLoading(false); }
  };
  return (
    <section className="bg-burgundy py-20 text-white" data-testid="newsletter-section">
      <div className="mx-auto max-w-2xl px-6 text-center">
        <h2 className="font-serif text-3xl text-white sm:text-4xl">Stay In Style</h2>
        <p className="mt-3 text-white/70">Get updates on new arrivals, exclusive offers and latest collections.</p>
        <form onSubmit={submit} className="mx-auto mt-8 flex max-w-md gap-2">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address"
            data-testid="newsletter-input" className="rounded-none border-white/30 bg-white/10 text-white placeholder:text-white/50 focus-visible:ring-white" />
          <Button type="submit" disabled={loading} data-testid="newsletter-submit" className="rounded-none bg-white text-burgundy hover:bg-rose">{loading ? "..." : "Subscribe"}</Button>
        </form>
      </div>
    </section>
  );
};

const ContactCTA = () => {
  const { settings, social } = useStore();
  const tel = `tel:${(settings.phone || "").replace(/\s/g, "")}`;
  return (
    <section className="bg-blush py-16" data-testid="contact-cta">
      <div className="mx-auto max-w-5xl px-6 text-center">
        <h2 className="font-serif text-3xl text-burgundy">Need Help Choosing Your Style?</h2>
        <p className="mt-2 font-script text-3xl text-charcoal/70">Connect with ANUSWA</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <a href={tel} data-testid="cta-call" className="group flex flex-col items-center gap-2 border border-burgundy/20 bg-white p-6 transition-colors hover:bg-cream">
            <Phone className="h-6 w-6 text-burgundy" />
            <span className="text-sm font-semibold uppercase tracking-wider">Call Us</span>
            <span className="text-sm text-muted-text">{settings.phone}</span>
          </a>
          <a href={waMessage(settings)} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="group flex flex-col items-center gap-2 border border-burgundy/20 bg-white p-6 transition-colors hover:bg-cream">
            <svg viewBox="0 0 32 32" className="h-6 w-6 fill-burgundy"><path d="M16 3C9 3 3.5 8.5 3.5 15.5c0 2.3.6 4.4 1.7 6.3L3 29l7.4-2.1c1.8 1 3.9 1.5 6 1.5 7 0 12.5-5.5 12.5-12.5S23 3 16 3z"/></svg>
            <span className="text-sm font-semibold uppercase tracking-wider">WhatsApp Us</span>
            <span className="text-sm text-muted-text">Chat directly with our shop</span>
          </a>
          <a href={social.instagram || "https://www.instagram.com/uniqueclothespalnpur/?hl=en"} target="_blank" rel="noopener noreferrer" data-testid="cta-instagram" className="group flex flex-col items-center gap-2 border border-burgundy/20 bg-white p-6 transition-colors hover:bg-cream">
            <Instagram className="h-6 w-6 text-burgundy" />
            <span className="text-sm font-semibold uppercase tracking-wider">Instagram</span>
            <span className="text-sm text-muted-text">Follow Unique Cloth Palanpur</span>
          </a>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { settings, social } = useStore();
  const [data, setData] = useState({ categories: [], newArrivals: [], trending: [], testimonials: [], promos: [] });

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/products?new_arrival=true&limit=8"),
      api.get("/products?trending=true&limit=8"),
      api.get("/testimonials"),
      api.get("/banners?type=promo"),
    ]).then(([c, n, t, tm, b]) => setData({ categories: c.data, newArrivals: n.data, trending: t.data, testimonials: tm.data, promos: b.data })).catch(() => {});
  }, []);

  const womensCats = data.categories.filter((c) => c.group === "clothing" || c.slug === "sandals");

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="grid lg:grid-cols-2">
          <div className="relative h-[380px] lg:h-[560px]">
            <img src={imgUrl("https://images.unsplash.com/photo-1631005436794-ccaa79de61ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000")} alt="ANUSWA fashion model" className="h-full w-full object-cover object-top" />
          </div>
          <div className="relative flex flex-col items-center justify-center bg-beige/60 px-8 py-14 text-center lg:py-0">
            <Leaf className="absolute right-4 top-6 h-40 w-24 text-burgundy/20 lg:right-10 lg:h-56 lg:w-32" />
            <Leaf className="absolute bottom-6 left-4 hidden h-32 w-20 rotate-180 text-burgundy/15 lg:block" />
            <h1 className="font-serif text-5xl font-bold tracking-[0.12em] text-burgundy sm:text-6xl lg:text-7xl">ANUSWA</h1>
            <p className="mt-2 font-script text-4xl text-charcoal/80 sm:text-5xl">Style That Speaks</p>
            <p className="mt-5 max-w-md text-sm text-charcoal sm:text-base">Ladies Clothing | Footwear | Fashion for Every You</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild className="rounded-none bg-charcoal px-7 py-6 text-sm uppercase tracking-widest hover:bg-burgundy" data-testid="hero-shop-now">
                <Link to="/new-arrivals">Shop Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-burgundy px-7 py-6 text-sm uppercase tracking-widest text-burgundy hover:bg-burgundy hover:text-white" data-testid="hero-explore">
                <Link to="/collections/kurtis">Explore Collection <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE COLLECTIONS */}
      <section className="bg-blush/40 py-14" data-testid="explore-collections">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-8 flex items-center gap-4">
            <p className="font-script text-3xl text-burgundy sm:text-4xl">Explore Our Collections</p>
          </div>
          <div className="no-scrollbar flex gap-5 overflow-x-auto pb-4 md:justify-center md:gap-8 md:flex-wrap md:overflow-visible">
            {(data.categories.length ? data.categories : CATEGORY_LIST).map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} className="group flex shrink-0 flex-col items-center gap-3" data-testid={`category-circle-${c.slug}`}>
                <div className="h-24 w-24 overflow-hidden rounded-full border-2 border-white shadow-sm ring-1 ring-border md:h-28 md:w-28">
                  <img src={imgUrl(c.image)} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <span className="text-sm font-medium text-charcoal group-hover:text-burgundy">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="py-16" data-testid="new-arrivals-section">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title="New Arrivals" link="/new-arrivals" />
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
            {data.newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6" data-testid="promo-banners">
        <div className="grid gap-5 md:grid-cols-2">
          {(data.promos.length ? data.promos : [
            { title: "UP TO 20% OFF", subtitle: "On Selected Styles", cta_text: "Shop Clothing", cta_link: "/collections/kurtis", image: "" },
            { title: "Step Into Style", subtitle: "Trendy Footwear for Every Occasion", cta_text: "Shop Footwear", cta_link: "/collections/sandals", image: "" },
          ]).map((b, i) => (
            <div key={i} className="relative flex h-64 items-center overflow-hidden bg-rose/40" data-testid={`promo-banner-${i}`}>
              <img src={imgUrl(b.image)} alt={b.title} className="absolute inset-0 h-full w-full object-cover opacity-90" />
              <div className="relative z-10 max-w-[55%] p-8">
                <p className="font-serif text-3xl font-bold text-burgundy sm:text-4xl">{b.title}</p>
                <p className="mt-1 text-sm text-charcoal">{b.subtitle}</p>
                <Button asChild className="mt-4 rounded-none bg-burgundy text-xs uppercase tracking-widest hover:bg-charcoal">
                  <Link to={b.cta_link || "/offers"}>{b.cta_text} <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SHOP WOMEN'S FASHION */}
      <section className="bg-cream py-16" data-testid="shop-womens-fashion">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title="Shop Women's Fashion" />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {womensCats.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} className="group relative h-72 overflow-hidden" data-testid={`shop-cat-${c.slug}`}>
                <img src={imgUrl(c.image)} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 flex flex-col items-center justify-end bg-gradient-to-t from-black/50 to-transparent p-5 text-center">
                  <h3 className="font-serif text-xl text-white">{c.name}</h3>
                  <span className="mt-1 text-xs uppercase tracking-widest text-white/90">Explore →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRENDING NOW */}
      <section className="py-16" data-testid="trending-section">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title="Trending Now" link="/offers" linkText="View All →" />
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
            {data.trending.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-y border-border bg-white py-10" data-testid="benefits-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 md:grid-cols-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="flex flex-col items-center gap-2 text-center">
              <b.icon className="h-7 w-7 text-burgundy" strokeWidth={1.5} />
              <p className="text-sm font-semibold uppercase tracking-wide text-charcoal">{b.title}</p>
              <p className="text-xs text-muted-text">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-beige/40 py-20" data-testid="about-section">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-3xl text-burgundy sm:text-4xl">About ANUSWA</h2>
          <span className="mx-auto mt-3 block h-0.5 w-16 bg-burgundy" />
          <p className="mt-6 text-base leading-relaxed text-charcoal">
            {settings.business_name || "ANUSWA by Unique Cloth"}, {settings.location || "Palanpur"} is a women's fashion destination offering thoughtfully curated clothing and footwear for women who want style, comfort and contemporary fashion.
          </p>
          <Button asChild variant="outline" className="mt-8 rounded-none border-burgundy text-burgundy hover:bg-burgundy hover:text-white" data-testid="about-know-more">
            <Link to="/about">Know More <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title="What Our Customers Say" center />
          <div className="grid gap-6 md:grid-cols-3">
            {data.testimonials.slice(0, 6).map((t) => (
              <div key={t.id} className="border border-border bg-white p-6" data-testid={`testimonial-${t.id}`}>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-amber-400 text-amber-400" : "text-border"}`} />)}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-charcoal">"{t.review}"</p>
                <p className="mt-4 font-serif text-lg text-burgundy">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="bg-cream py-16" data-testid="instagram-section">
        <div className="mx-auto max-w-7xl px-4 text-center md:px-6">
          <h2 className="font-serif text-3xl text-burgundy sm:text-4xl">Follow ANUSWA</h2>
          <p className="mt-2 text-muted-text">Discover the latest styles, looks and collections.</p>
          <div className="mt-8 grid grid-cols-3 gap-2 md:grid-cols-6">
            {["kurti2", "dress2", "coord2", "trad2", "heel1", "sneaker3"].map((k, i) => (
              <a key={i} href={social.instagram || "https://www.instagram.com/uniqueclothespalnpur/?hl=en"} target="_blank" rel="noopener noreferrer" className="group relative aspect-square overflow-hidden">
                <img src={imgUrl(INSTA[k])} alt="ANUSWA style" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <span className="absolute inset-0 grid place-items-center bg-burgundy/0 opacity-0 transition-all group-hover:bg-burgundy/40 group-hover:opacity-100"><Instagram className="h-6 w-6 text-white" /></span>
              </a>
            ))}
          </div>
          <Button asChild className="mt-8 rounded-none bg-burgundy hover:bg-charcoal" data-testid="follow-instagram-btn">
            <a href={social.instagram || "https://www.instagram.com/uniqueclothespalnpur/?hl=en"} target="_blank" rel="noopener noreferrer"><Instagram className="mr-2 h-4 w-4" /> Follow Us on Instagram</a>
          </Button>
        </div>
      </section>

      <Newsletter />
      <ContactCTA />
    </div>
  );
};

const INSTA = {
  kurti2: "https://images.unsplash.com/photo-1708534419572-6e6614a53ca1?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  dress2: "https://images.unsplash.com/photo-1602303894456-398ce544d90b?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  coord2: "https://images.unsplash.com/photo-1578050935158-82d21c076a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  trad2: "https://images.unsplash.com/photo-1737514996816-a034a795febe?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  heel1: "https://images.unsplash.com/photo-1789110519282-744f4955e1ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  sneaker3: "https://images.unsplash.com/photo-1720019315323-5e2b98a58ba8?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
};

export default Home;
export { Newsletter, ContactCTA, SectionHeading };
